import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getUserDisplayName, getWorkDomainFromEmail } from '@gitbook/app-database';
import { toast } from '@gitbook/spine-react';

import { apiClient } from '../../state';
import { capitalizeFirstLetter } from '../../utils';

/**
 * Create the default docs site used by the welcome onboarding flow.
 */
export async function createOnboardingSite({
    organization,
}: {
    organization: api.Organization;
}): Promise<api.Site | undefined> {
    return await toast.catch(async () => {
        const { data: site } = await apiClient.sites.createSite(organization.id, {
            title: `${organization.title} Docs`,
        });

        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organization.id },
                {
                    type: 'site_create_from_wizard',
                    inputName: site.title,
                    inputContent: 'empty',
                }
            )
        );

        return site;
    }, 'Error while creating site');
}

/**
 * Get an initial name for organization.
 */
export function getOrgInitialTitle(user: {
    displayName?: string | null;
    email?: string | null;
}): string {
    if (user.email) {
        const workEmail = getWorkDomainFromEmail(user.email);
        if (workEmail) {
            return capitalizeFirstLetter(workEmail.replace(/\.[a-z0-9]+$/, ''));
        }
    }

    if (user.displayName) {
        return `${capitalizeFirstLetter(
            extractFirstName(getUserDisplayName(user))
        )}'s Organization`;
    }

    return '';
}

function extractFirstName(input: string): string {
    return input.split(/\s|\+/)[0] || '';
}
